import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getStoredToken, getStoredUser } from "../utils/helpers";
import { syncCompanionSession, clearCompanionSession } from "../services/uploadService";

const AuthContext = createContext(null);
const SESSION_INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "ps_last_activity_at";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
  "focus",
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [token, setToken] = useState(getStoredToken);
  const idleTimerRef = useRef(null);

  const login = useCallback((userData, authToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
    setUser(userData);
    setToken(authToken);
    syncCompanionSession().catch(() => {});
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    setUser(null);
    setToken(null);
    clearCompanionSession().catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      return;
    }

    let lastActivityAt = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
    if (!Number.isFinite(lastActivityAt) || lastActivityAt <= 0) {
      lastActivityAt = Date.now();
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(lastActivityAt));
    }

    const scheduleIdleLogout = () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);

      const elapsedMs = Date.now() - lastActivityAt;
      const remainingMs = SESSION_INACTIVITY_TIMEOUT_MS - elapsedMs;

      if (remainingMs <= 0) {
        logout();
        return;
      }

      idleTimerRef.current = window.setTimeout(logout, remainingMs);
    };

    const recordActivity = () => {
      if (Date.now() - lastActivityAt >= SESSION_INACTIVITY_TIMEOUT_MS) {
        logout();
        return;
      }

      lastActivityAt = Date.now();
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(lastActivityAt));
      scheduleIdleLogout();
    };

    scheduleIdleLogout();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });

    return () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
    };
  }, [logout, token]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
